import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton,
} from '@mui/material';
import { Close, AdminPanelSettings } from '@mui/icons-material';
import { collection, query, where, getDocs } from '../services/firestoreCompat';
import { db, HAS_VALID_FIREBASE_CONFIG } from '../services/firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';

export interface ManagerPinDialogProps {
  open: boolean;
  /** Action requiring approval e.g. "Refund €50.00" */
  actionLabel: string;
  /** Optional reason field — required for cancellations */
  requireReason?: boolean;
  onApproved: (managerEmail: string, reason: string) => void;
  onClose: () => void;
}

/**
 * FRAUD PREVENTION #2: Manager PIN dialog
 * Cashier must enter manager's 4-6 digit PIN to authorize critical actions:
 *   - Refunds
 *   - Discounts > 10%
 *   - Voiding orders
 *   - Deleting items after kitchen prints
 *
 * PIN is stored on each manager/admin user document as `manager_pin` (hashed/plain).
 * Defaults to admin password last 4 digits if not set.
 */
const ManagerPinDialog: React.FC<ManagerPinDialogProps> = ({ open, actionLabel, requireReason = false, onApproved, onClose }) => {
  const { t } = useLanguage();
  const [pin, setPin] = useState(['', '', '', '']);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

  useEffect(() => {
    if (open) {
      setPin(['', '', '', '']);
      setReason('');
      setError('');
      // If a reason is required, focus the reason field so the manager can
      // finish typing a complete sentence before dealing with the PIN.
      // Otherwise jump straight to the PIN.
      setTimeout(() => {
        if (requireReason) {
          const reasonInput = document.querySelector<HTMLInputElement>('input[data-role="manager-pin-reason"]');
          reasonInput?.focus();
        } else {
          inputRefs[0].current?.focus();
        }
      }, 100);
    }
  }, [open, requireReason]); // eslint-disable-line

  const handleDigit = (idx: number, val: string) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const next = [...pin]; next[idx] = val; setPin(next);
    if (val && idx < 3) inputRefs[idx + 1].current?.focus();
    if (val && idx === 3 && next.every(d => d !== '')) verify(next.join(''));
  };

  const handleKey = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[idx] && idx > 0) inputRefs[idx - 1].current?.focus();
  };

  // Was previously auto-verifying on every reason keystroke once the PIN was
  // complete — that closed the dialog after the user typed a single character.
  // Removed. Verification now fires on:
  //   1. 4th PIN digit entry (when reason is already filled), OR
  //   2. Enter key in the reason field, OR
  //   3. explicit Verify button click.

  // GoBD: persist every manager-PIN verification to the audit log.
  // Wraps onApproved so EVERY success path emits the event without
  // sprinkling `await callable(...)` across 4 different branches.
  const recordAuditAndApprove = async (managerEmail: string, reasonText: string) => {
    if (!HAS_VALID_FIREBASE_CONFIG) {
      onApproved(managerEmail, reasonText);
      return;
    }
    try {
      const { httpsCallable, getFunctions } = await import('firebase/functions');
      const callable = httpsCallable(getFunctions(), 'logAuditEvent');
      await callable({
        action: 'manager_pin_verified',
        reason: reasonText,
        target: actionLabel,
        metadata: {
          authorized_email: managerEmail,
          required_reason: requireReason,
        },
      });
    } catch (auditErr) {
      console.warn('[AUDIT] manager_pin_verified emit failed (non-fatal):', auditErr);
    }
    onApproved(managerEmail, reasonText);
  };

  const recordAuditFail = async (failReason: string) => {
    if (!HAS_VALID_FIREBASE_CONFIG) return;
    try {
      const { httpsCallable, getFunctions } = await import('firebase/functions');
      const callable = httpsCallable(getFunctions(), 'logAuditEvent');
      await callable({
        action: 'manager_pin_failed',
        reason: failReason,
        target: actionLabel,
      });
    } catch (_) { /* swallow */ }
  };

  const verify = async (enteredPin: string) => {
    if (requireReason && !reason.trim()) {
      setError(t('manager_pin.reason_required'));
      return;
    }
    setVerifying(true);
    setError('');
    try {
      console.log('[MANAGER PIN] verifying', enteredPin.replace(/./g, '*'));
      const adminEmails = ['admin@saheehpos.com', 'admin@saheehpos.com', 'owner@saheehpos.com', 'manager@saheehpos.com'];

      // Helper — an entry authorizes if the email is in the admin whitelist
      // OR the role is admin/manager. The whitelist guards against cases where
      // users/{uid}.role got stomped to 'customer' during a sync (we saw this
      // in listAuthUsers which falls back to 'customer' when firestoreData has
      // no role — the admin-UI savePin then wrote role='customer' on merge).
      const isAuthorized = (data: any): boolean => {
        const email = (data.email || '').toLowerCase();
        const role = (data.role || '').toLowerCase();
        if (adminEmails.includes(email)) return true;
        return ['admin', 'manager'].includes(role);
      };

      // 1) users/* by manager_pin (primary store — admin-UI writes here first)
      try {
        const snap = await getDocs(query(collection(db, 'users'), where('manager_pin', '==', enteredPin)));
        if (!snap.empty) {
          const userData = snap.docs[0].data();
          console.log('[MANAGER PIN] users/ matched', userData.email, 'role', userData.role);
          if (isAuthorized(userData)) {
            await recordAuditAndApprove(userData.email || '', reason.trim());
            return;
          }
          console.log('[MANAGER PIN] matched but role/email not authorized — falling through');
        }
      } catch (e) { console.warn('[MANAGER PIN] users/ lookup failed', e); }

      // 2) admin_users/* by manager_pin (mirror)
      try {
        const adminSnap = await getDocs(query(collection(db, 'admin_users'), where('manager_pin', '==', enteredPin)));
        if (!adminSnap.empty) {
          const userData = adminSnap.docs[0].data();
          console.log('[MANAGER PIN] admin_users/ matched', userData.email);
          if (isAuthorized(userData)) {
            await recordAuditAndApprove(userData.email || '', reason.trim());
            return;
          }
        }
      } catch (e) { console.warn('[MANAGER PIN] admin_users/ lookup failed', e); }

      // 3) pos_users/* by manager_pin — some setups store staff there instead
      try {
        const posSnap = await getDocs(query(collection(db, 'pos_users'), where('manager_pin', '==', enteredPin)));
        if (!posSnap.empty) {
          const userData = posSnap.docs[0].data();
          console.log('[MANAGER PIN] pos_users/ matched', userData.email);
          if (isAuthorized(userData)) {
            await recordAuditAndApprove(userData.email || '', reason.trim());
            return;
          }
        }
      } catch (e) { console.warn('[MANAGER PIN] pos_users/ lookup failed', e); }

      // 3) Master PIN — emergency fallback so the team is never locked out.
      // Change this value in src/components/ManagerPinDialog.tsx and redeploy
      // when a manager leaves / PIN leaks.
      const MASTER_PIN = '2025';
      if (enteredPin === MASTER_PIN) {
        console.log('[MANAGER PIN] approved via MASTER fallback');
        await recordAuditAndApprove('admin@saheehpos.com', reason.trim());
        return;
      }

      // Nothing matched — show a specific, actionable error
      setError(t('manager_pin.invalid'));
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
      // GoBD: log failed PIN attempts so brute-force is detectable.
      await recordAuditFail('Invalid PIN entered');
    } catch (err: any) {
      console.error('[MANAGER PIN] verify error:', err);
      setError(err?.message || t('manager_pin.verify_failed'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      disableScrollLock
      PaperProps={{ sx: { borderRadius: 3, bgcolor: '#1a1d2e', border: '2px solid #FFC107' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid #2a2d42', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AdminPanelSettings sx={{ color: '#FFC107', fontSize: 28 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>{t('manager_pin.title')}</Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>{actionLabel}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#9ca3af' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {requireReason && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mb: 0.5 }}>{t('manager_pin.reason_label')}</Typography>
            <input
              type="text" value={reason}
              onChange={(e) => { setReason(e.target.value); if (error) setError(''); }}
              onKeyDown={(e) => {
                // Enter submits — only if PIN is also complete. Otherwise just
                // shift focus to the first PIN input. Never auto-submits mid-typing.
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const fullPin = pin.join('');
                if (fullPin.length === 4 && reason.trim()) {
                  verify(fullPin);
                } else if (fullPin.length < 4) {
                  inputRefs[0].current?.focus();
                }
              }}
              placeholder={t('manager_pin.reason_placeholder')}
              data-role="manager-pin-reason"
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid #3a3d52', background: '#1e2130', color: '#fff',
                fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </Box>
        )}

        <Typography sx={{ color: '#fff', fontSize: '0.9rem', mb: 1.5, textAlign: 'center' }}>
          {t('manager_pin.enter_pin')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 2 }}>
          {pin.map((digit, i) => (
            <input
              key={i} ref={inputRefs[i]}
              type="password" inputMode="numeric" maxLength={1}
              value={digit}
              onChange={(e) => handleDigit(i, e.target.value)}
              onKeyDown={(e) => handleKey(i, e)}
              disabled={verifying}
              style={{
                width: 56, height: 64, textAlign: 'center', fontSize: '2rem',
                fontWeight: 700, borderRadius: 12,
                border: `2px solid ${digit ? '#FFC107' : '#3a3d52'}`,
                background: '#1e2130', color: '#FFC107',
                outline: 'none', transition: 'border-color 0.2s',
              }}
            />
          ))}
        </Box>

        {error && (
          <Typography sx={{ color: '#ef5350', fontSize: '0.8rem', textAlign: 'center', mt: 1 }}>{error}</Typography>
        )}

        {verifying && (
          <Typography sx={{ color: '#FFC107', fontSize: '0.8rem', textAlign: 'center', mt: 1 }}>{t('manager_pin.verifying')}</Typography>
        )}

        <Typography sx={{ color: '#666', fontSize: '0.7rem', textAlign: 'center', mt: 2, fontStyle: 'italic' }}>
          🔒 {t('manager_pin.authorize_note')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #2a2d42', px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} disabled={verifying} sx={{ color: '#9ca3af', textTransform: 'none' }}>{t('manager_pin.cancel')}</Button>
        {/* Explicit verify button — either/or ordering is fine, click this when done. */}
        <Button
          onClick={() => verify(pin.join(''))}
          disabled={verifying || pin.join('').length !== 4 || (requireReason && !reason.trim())}
          variant="contained"
          sx={{
            bgcolor: '#FFC107', color: '#1a1d2e', fontWeight: 700, textTransform: 'none',
            '&:hover': { bgcolor: '#FFB300' },
            '&.Mui-disabled': { bgcolor: 'rgba(255,193,7,0.3)', color: 'rgba(26,29,46,0.6)' },
          }}
        >
          {verifying ? t('manager_pin.verifying') : t('manager_pin.approve')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerPinDialog;
